import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import CheckboxInput from "@galaxy-io/dls/inputs/CheckboxInput";
import Flex, { AlignItems } from "@galaxy-io/dls/layout/Flex";
import Text from "@galaxy-io/dls/text/Text";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import { ProhibitIcon, WebhooksLogoIcon } from "@phosphor-icons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/workspace/select-field";
import { SourceIcon } from "@/components/workspace/source-icon";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";

type Destination = { id: string; name: string; provider: "slack" | "webhook" };
export function SendMenu({
  keys,
  disabled = false,
  onSent,
  bulk = false,
  textOnly = false,
  onIgnore,
  fillWidth = false,
  onPendingChange,
}: {
  keys: string[];
  disabled?: boolean;
  onSent?: () => void;
  bulk?: boolean;
  textOnly?: boolean;
  onIgnore?: () => void;
  fillWidth?: boolean;
  onPendingChange?: (pending: boolean) => void;
}) {
  const router = useRouter(),
    { toast } = useToast();
  const [selection, setSelection] = useState<string[] | null>(null);
  const [destinationId, setDestinationId] = useState("");
  const [includeSignalPayload, setIncludeSignalPayload] = useState(false);
  const signalSelection = keys.some((key) => key.startsWith("observation:"));
  function close() {
    setSelection(null);
    onPendingChange?.(false);
  }
  const destinations = useQuery<Destination[]>({
    queryKey: ["delivery-destinations"],
    staleTime: 30000,
    refetchOnMount: "always",
    queryFn: async () => {
      return unwrap(apiClient().GET("/deliveries/destinations"));
    },
  });
  const availableDestinations = destinations.data?.filter(
    (d) => !signalSelection || d.provider === "webhook",
  );
  const send = useMutation({
    onMutate: () => onPendingChange?.(true),
    mutationFn: async (integrationId: string) => {
      return unwrap(
        apiClient().POST("/deliveries", {
          body: {
            keys: selection || [],
            integrationId,
            ...(signalSelection ? { includeSignalPayload } : {}),
          },
        }),
      );
    },
    onSuccess: (result) => {
      window.dispatchEvent(new CustomEvent("records:changed", { detail: result.states }));
      router.refresh();
      toast({
        variant: ToastVariant.SUCCESS,
        header: result.queued
          ? `Sending to ${result.destination}`
          : `Already sent to ${result.destination}`,
        description: result.queued
          ? `${result.queued} ${result.queued === 1 ? "person" : "people"}${result.existing ? ` · ${result.existing} already sent` : ""}`
          : "See delivery history to inspect or retry.",
        ...toastDefaults(ToastVariant.SUCCESS),

        action: {
          label: "View history",
          onClick: () => router.push("/workflows?kind=delivery&view=history"),
        },
      });
      close();
      onSent?.();
    },
    onError: (error) =>
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not send people",
        description: error.message,
        ...toastDefaults(ToastVariant.ERROR),
      }),
  });
  const size = bulk ? ButtonSize.SMALL : ButtonSize.MEDIUM;
  const disabledAction = disabled || !keys.length || send.isPending;
  return (
    <>
      <SurfaceDialog
        open={selection !== null}
        title={signalSelection ? "Send signals to webhook" : "Send people"}
        onClose={close}
        busy={send.isPending}
        footer={
          <Button
            size={size}
            label="Send"
            isLoading={send.isPending}
            isDisabled={
              send.isPending ||
              destinations.isPending ||
              destinations.isError ||
              !selection?.length ||
              !availableDestinations?.some((d) => d.id === destinationId)
            }
            onClick={() => send.mutate(destinationId)}
          />
        }
      >
        <Text>
          {signalSelection
            ? `Send the people associated with ${selection?.length} selected signals. Each person is sent once. Statuses stay unchanged.`
            : `Send ${selection?.length} selected ${selection?.length === 1 ? "person" : "people"}.`}
        </Text>
        {destinations.isPending || destinations.isError ? (
          <QueryState
            error={destinations.error}
            loadingMessage="Loading destinations…"
            onRetry={() => void destinations.refetch()}
          />
        ) : !availableDestinations?.length ? (
          <Button label="Connect an integration" onClick={() => router.push("/integrations")} />
        ) : (
          <SelectField
            label="Destination"
            value={destinationId}
            disabled={send.isPending}
            options={availableDestinations.map((d) => ({
              value: d.id,
              label: d.name,
              icon: <SourceIcon source={d.provider} />,
            }))}
            onChange={setDestinationId}
          />
        )}
        {signalSelection && (
          <CheckboxInput
            label="Include signal payload"
            isChecked={includeSignalPayload}
            isDisabled={send.isPending}
            onChange={setIncludeSignalPayload}
          />
        )}
      </SurfaceDialog>
      <Flex
        alignItems={AlignItems.CENTER}
        gap={8}
        maxWidth="100%"
        width={fillWidth ? "100%" : "max-content"}
        minWidth={0}
      >
        <Flex minWidth={0} width={fillWidth ? "100%" : undefined}>
          <Button
            size={size}
            label={textOnly ? "Send" : bulk ? "Send selected…" : "Send…"}
            icon={textOnly ? undefined : WebhooksLogoIcon}
            variant={ButtonVariant.SECONDARY}
            fillWidth={fillWidth}
            isDisabled={disabledAction}
            onClick={() => {
              setSelection([...keys]);
              setDestinationId(
                availableDestinations?.length === 1 ? availableDestinations[0].id : "",
              );
              setIncludeSignalPayload(false);
              onPendingChange?.(true);
              void destinations.refetch();
            }}
          />
        </Flex>
        {onIgnore && (
          <Button
            size={size}
            ariaLabel="Ignore"
            icon={ProhibitIcon}
            variant={ButtonVariant.TERTIARY}
            isDisabled={disabledAction}
            onClick={onIgnore}
            tooltip="Ignore"
          />
        )}
      </Flex>
    </>
  );
}
